<template>
  <v-row>
    <v-col class="py-0" cols="12">
      <v-btn
        color="warning"
        fixed
        bottom
        right
        fab
        title="Apply Changes"
        @click="applyChanges()"
        v-if="needsApply"
      >
        <v-icon large>mdi-restart</v-icon>
      </v-btn>

      <v-form ref="form" v-model="valid" @submit.prevent="saveAction" lazy-validation>
        <v-card :loading="saving">
          <v-card-title>
            <v-icon v-if="!action.__hasChanges">mdi-arrow-right-circle</v-icon>
            <v-icon v-if="action.__hasChanges" color="primary">mdi-content-save-alert-outline</v-icon>
            <span class="pl-2 headline">
              Action
              <span class="pl-2 subtitle-1">
                <TimeoutMessage v-model="saving">
                  <span class="primary--text">Saving...</span>
                </TimeoutMessage>
              </span>
            </span>
          </v-card-title>
          <v-card-text>
            <v-alert dense type="warning" v-if="errors.server">{{errors.server}}</v-alert>
            <v-alert border="left" color="warning" type="warning" dense text colored-border v-if="needsApply">
              <v-row align="center">
                <v-col class="grow">Your <b>action</b> has changes that need to be applied...</v-col>
                <v-col class="shrink">
                  <v-btn color="warning" @click="applyChanges()"><v-icon>mdi-restart</v-icon> Apply</v-btn>
                </v-col>
              </v-row>
            </v-alert>
            <v-container>
              <v-row align="start">
                <v-col cols="9">
                  <v-text-field
                    label="Name*"
                    hint="A short name used to identify this action"
                    v-model="action.name"
                    @blur="titleCase('name')"
                    validate-on-blur
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="3" align-self="center" style="position: relative;">
                  <div class="d-flex align-center justify-center">
                    <label>Icon</label>
                    <v-btn icon @click="setIcon = !setIcon">
                      <v-icon>mdi-sticker-emoji</v-icon>
                    </v-btn>
                    <v-avatar
                      color="grey lighten-1"
                      size="48"
                      class="ml-4"
                      v-if="action.properties.icon"
                    >
                      <span class="text-h5 ma-0 pa-0">{{action.properties.icon}}</span>
                    </v-avatar>
                    <emoji
                      position="left"
                      :show.sync="setIcon"
                      :emoji-url.sync="action.properties.iconUrl"
                      :prepend-to.sync="action.properties.icon"
                    ></emoji>
                  </div>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    label="Description*"
                    v-model="action.description"
                    :error-messages="errors.email"
                    validate-on-blur
                    required
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row align="start">
                <v-col cols="12" md="12">
                  <v-subheader class="title">Trigger</v-subheader>
                  <v-divider></v-divider>

                  <v-row>
                    <v-col class="mb-0 pb-0" cols="12" md="12">
                      <v-radio-group v-model="action.trigger.type" :mandatory="true" :row="true">
                        <v-radio label="Command" value="command"></v-radio>
                        <v-radio label="Keyword/s" value="keywords"></v-radio>
                        <v-radio label="Media" value="media"></v-radio>
                        <v-radio label="Special" value="special"></v-radio>
                        <v-radio label="Schedule" value="schedule" v-if="isScheduledActionsEnabled"></v-radio>
                      </v-radio-group>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col class="mt-0 pt-0 mb-0 pb-0" cols="12" md="12">
                      <v-text-field
                        label="Command*"
                        hint="A command constitutes a single word that describes the intended action"
                        v-model="action.trigger.command"
                        :rules="commandRules"
                        v-if="action.trigger.type === 'command'"
                        validate-on-blur
                        required
                      >
                        <v-icon slot="prepend" color="primary">mdi-slash-forward</v-icon>
                      </v-text-field>

                      <v-combobox
                        v-model="action.trigger.keywords"
                        :items="keywords"
                        v-if="action.trigger.type === 'keywords'"
                        :deletable-chips="true"
                        chips
                        label="Keyword/s*"
                        append-icon
                        :delimiters="[',']"
                        :counter="5"
                        :rules="keywordsRules"
                        hint="Enter up to 5 keywords that if found in a message will trigger the action. Press enter (or comma) after each keyword."
                        multiple
                      ></v-combobox>

                      <v-select
                        v-model="action.trigger.media"
                        :items="media"
                        v-if="action.trigger.type === 'media'"
                        attach
                        label="Media*"
                        hint="Choose what type of media will trigger the action."
                        persistent-hint
                        validate-on-blur
                      ></v-select>

                      <v-select
                        v-model="action.trigger.special"
                        :items="special"
                        v-if="action.trigger.type === 'special'"
                        attach
                        label="Special*"
                        hint="Choose what type of special content will trigger the action."
                        persistent-hint
                        validate-on-blur
                      ></v-select>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col class="mt-0 pt-0" cols="12" md="6">
                      <v-checkbox
                        v-if="action.trigger.type !== 'schedule'"
                        v-model="action.members_only"
                        label="Members Only"
                        :hint="action.members_only ? 'Only members (users that have registered with the bot) in the trigger group/s can trigger this action.'
                      :'Anyone in the trigger group/s can trigger this action.'"
                        persistent-hint
                      ></v-checkbox>
                      <v-radio-group
                        v-if="action.members_only"
                        v-model="action.properties.which_members"
                        mandatory
                      >
                        <v-radio
                          label="All Members"
                          value="all_members"
                        ></v-radio>
                        <v-radio
                          label="Verified Members"
                          value="verified_members"
                        ></v-radio>
                      </v-radio-group>
                    </v-col>
                    <v-col
                      class="mt-0 pt-0"
                      cols="12"
                      :md="action.trigger.type === 'schedule' ? 12 : 6"
                    >
                      <v-checkbox
                        v-model="action.trigger.enabled"
                        label="Enabled"
                        :hint="action.trigger.enabled ? 'This action is enabled and will trigger as configured.'
                      :'This action is now disabled and will not trigger.'"
                        persistent-hint
                      ></v-checkbox>
                    </v-col>
                    <v-col
                      class="mt-0 pt-0"
                      cols="12"
                      md="6"
                      v-if="action.trigger.type !== 'schedule'"
                    >
                      <v-checkbox
                        v-model="action.properties.hidden"
                        label="Hidden"
                        :hint="action.properties.hidden ? 'This action will not be listed in the help menu.'
                      :'This action will be listed in the help menu.'"
                        persistent-hint
                      ></v-checkbox>
                    </v-col>
                    <v-col
                      class="mt-0 pt-0"
                      cols="12"
                      md="6"
                      v-if="action.trigger.type !== 'schedule'"
                    >
                      <v-checkbox
                        v-model="action.properties.menu"
                        label="Menu"
                        :hint="action.properties.menu ? 'This action will be shown in the bot\'s main menu.'
                      :'This action will NOT be shown in the bot\'s main menu.'"
                        persistent-hint
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <v-row align="start" v-if="action.trigger && action.trigger.type === 'schedule'">
                <v-col class="pb-0" cols="12">
                  <v-subheader class="title">Schedule</v-subheader>
                  <v-divider></v-divider>
                  <p
                    class="mt-2"
                  >When scheduled, this action will trigger on the set interval from the start date selected on the given time.</p>
                </v-col>
                <v-col class="px-6" cols="12" md="4">
                  <PickDate
                    v-model="action.trigger.date"
                    label="Pick a start date for your trigger schedule"
                  ></PickDate>
                </v-col>
                <v-col class="px-6" cols="12" md="4">
                  <PickTime
                    v-model="action.trigger.time"
                    label="Pick a time for the action to trigger"
                  ></PickTime>
                </v-col>
                <v-col class="px-6" cols="12" md="4">
                  <v-select
                    :items="scheduleIntervals"
                    v-model="action.trigger.interval"
                    label="Interval"
                    prepend-icon="mdi-update"
                  ></v-select>
                </v-col>
              </v-row>

              <v-row align="start" v-if="action.trigger && action.trigger.type !== 'media'">
                <v-col cols="12">
                  <v-subheader class="title">Elaborator</v-subheader>
                  <v-divider></v-divider>
                  <p
                    class="mt-2"
                  >Elborators are custom prompt sequences that lets user elaborate on their trigger message/command.</p>
                  <p>Resulting messages will be used in the forward action and will also be logged together with the trigger/original message.</p>
                </v-col>
                <v-col class="mt-0 pt-0" cols="12">
                  <v-select
                    :label="`Elaborator${elboratorRequired ? '*':''}`"
                    v-model="action.elaborator_id"
                    item-value="id"
                    :items="elaborators"
                    :clearable="true"
                    :rules="elaboratorRules"
                    hint="Choose the elaborator that will run before the other actions are executed"
                  >
                    <template slot="selection" slot-scope="data">{{ data.item.name }}</template>
                    <template slot="item" slot-scope="data">
                      <template>
                        <v-list-item-content>
                          <v-list-item-title v-html="data.item.name"></v-list-item-title>
                          <v-list-item-subtitle v-html="data.item.description"></v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </template>
                  </v-select>
                  <v-banner class="mt-4" single-line v-if="selectedElaborator">
                    <v-icon slot="icon" color="accent" size="36">mdi-plus-box-multiple</v-icon>
                    <span>
                      <span class="subtitle-1">{{selectedElaborator.name}}:</span>
                      {{selectedElaborator.description}}
                    </span>
                  </v-banner>
                </v-col>
              </v-row>

              <v-row align="start">
                <v-col cols="12" md="12">
                  <v-alert
                    border="left"
                    color="error"
                    dark
                    v-if="error.errors && error.errors.steps"
                  >{{error.errors.steps}}</v-alert>
                  <v-subheader class="title">Action</v-subheader>
                  <v-divider></v-divider>
                  <v-banner 
                    single-line 
                    v-if="action.trigger && (action.trigger.type !== 'media' || (action.trigger.type === 'media' && action.trigger.media !== 'voice')) && !action.actions.find">
                    <v-icon slot="icon" color="grey" size="36">mdi-text-box-outline</v-icon>
                    <span>Save the message in your message log which can be viewed on the messages page.</span>
                    <template v-slot:actions>
                      <span class="subtitle-1 font-weight-medium grey--text" color="grey">Log</span>
                      <v-switch
                        v-model="action.actions.log"
                        class="ml-4"
                        inset
                      ></v-switch>
                    </template>
                    <v-expansion-panels v-show="action.actions.log">
                      <v-expansion-panel>
                        <v-expansion-panel-header class="px-0">
                          <template v-slot:default="{ open }">
                            <v-row no-gutters>
                              <v-col cols="4">
                                Reference number
                              </v-col>
                              <v-col
                                cols="8"
                                class="text--secondary"
                              >
                                <v-fade-transition leave-absolute>
                                  <span
                                    v-if="open"
                                    key="0"
                                  >
                                    Add a reference number for messages logged for this action
                                  </span>
                                  <span
                                    v-else
                                    key="1"
                                  >
                                    Reference number settings
                                  </span>
                                </v-fade-transition>
                              </v-col>
                            </v-row>
                          </template>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <v-checkbox
                            v-model="action.properties.reference"
                            label="Set Reference"
                            hint="A reference number will be generated and sent to the user once action has completed"
                            persistent-hint
                          ></v-checkbox>
                          <v-text-field
                            class="mt-6"
                            label="Prepend to Reference"
                            hint="Enter a 1-3 letter code to prepend to the reference code (auto generated)"
                            v-model="action.properties.reference_prepend"
                            :rules="referenceRules"
                            v-if="action.properties.reference"
                            validate-on-blur
                            required
                          >
                            <span slot="append" color="primary">{{action.properties.reference_prepend}}********</span>
                          </v-text-field>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-banner>

                  <v-banner single-line v-if="!action.elaborator_id">
                    <v-icon slot="icon" color="grey" size="36">mdi-comment-remove-outline</v-icon>
                    <span>Message will be removed from the group.</span>
                    <template v-slot:actions>
                      <span class="subtitle-1 font-weight-medium grey--text" color="grey">Remove</span>
                      <v-switch v-model="action.actions.remove" class="ml-4" inset></v-switch>
                    </template>
                  </v-banner>

                  <v-banner two-line v-if="!action.elaborator_id">
                    <v-icon slot="icon" color="grey" size="36">mdi-spellcheck</v-icon>
                    <span>Message will be removed from the group pending admin approval.</span>
                    <br />
                    <span
                      class="grey--text"
                      v-if="action.actions.approve && action.elaborator_id"
                    >The compiled elaborator message will not be sent unless approved</span>
                    <span
                      class="grey--text"
                      v-if="action.actions.approve && !action.elaborator_id"
                    >The message will be removed from the group and resent if approved</span>
                    <template v-slot:actions>
                      <span class="subtitle-1 font-weight-medium grey--text" color="grey">Approve</span>
                      <v-switch v-model="action.actions.approve" class="ml-4" inset></v-switch>
                    </template>
                  </v-banner>

                  <v-banner single-line v-if="!action.actions.find">
                    <v-icon slot="icon" color="grey" size="36">mdi-share</v-icon>
                    <span>Forward the message or the elaborated message to any chosen group/s.</span>
                    <template v-slot:actions>
                      <span class="subtitle-1 font-weight-medium grey--text" color="grey">Forward</span>
                      <v-switch
                        v-model="action.actions.forward"
                        class="ml-4"
                        inset
                      ></v-switch>
                    </template>
                  </v-banner>

                  <v-banner single-line v-if="action.elaborator_id">
                    <v-icon slot="icon" color="grey" size="36">mdi-reply</v-icon>
                    <span>Send resulting elaborated message to the user that initiated the action.</span>
                    <template v-slot:actions>
                      <span class="subtitle-1 font-weight-medium grey--text" color="grey">Send Result</span>
                      <v-switch
                        v-model="action.actions.send_result"
                        class="ml-4"
                        inset
                      ></v-switch>
                    </template>
                  </v-banner>

                  <v-banner 
                    single-line 
                    v-if="isWebhooksEnabled && action.trigger && (action.trigger.type !== 'media' || (action.trigger.type === 'media' && action.trigger.media !== 'voice')) && !action.actions.find">
                    <v-icon slot="icon" color="grey" size="36">mdi-upload-network-outline</v-icon>
                    <span>Send the message, elbaorated message and data to your own API endpoint.</span>
                    <br />
                    <span
                      class="grey--text"
                    >The compiled elaborator message will not be sent unless approved</span>
                    <template v-slot:actions>
                      <span class="subtitle-1 font-weight-medium grey--text" color="grey">Webhook</span>
                      <v-switch v-model="action.actions.webhook" class="ml-4" inset></v-switch>
                    </template>
                  </v-banner>

                  <v-banner single-line v-if="isActionsMapEnabled && action.elaborator_id && !action.actions.find">
                    <v-icon slot="icon" color="grey" size="36">mdi-map-marker-down</v-icon>
                    <span>Real-time view of actions triggered.</span>
                    <br />
                    <span
                      class="grey--text"
                    >Action must have an elaborator that includes a location prompt</span>
                    <template v-slot:actions>
                      <span class="subtitle-1 font-weight-medium grey--text" color="grey">Map</span>
                      <v-switch v-model="action.properties.map" class="ml-4" inset></v-switch>
                    </template>
                  </v-banner>

                  <v-banner single-line v-if="isProcessFilesEnabled && action.elaborator_id">
                    <v-icon slot="icon" color="grey" size="36">mdi-file</v-icon>
                    <span>Process files with custom file processors.</span>
                    <br />
                    <span
                      class="grey--text"
                    >Action must have an elaborator that includes a file and custom file processor must be coded</span>
                    <template v-slot:actions>
                      <span class="subtitle-1 font-weight-medium grey--text" color="grey">Process File</span>
                      <v-switch v-model="action.actions.process_file" class="ml-4" inset></v-switch>
                    </template>
                  </v-banner>

                  <v-banner single-line v-if="action.elaborator_id && elaboratorPromptFind && elaboratorPromptFind.length">
                    <v-icon slot="icon" color="grey" size="36">mdi-text-box-search-outline</v-icon>
                    <span>Find logged messages matching value.</span>
                    <br />
                    <span
                      class="grey--text"
                    >Action must have an elaborator that includes a prompt type of one of the types in the select</span>
                    <template v-slot:actions>
                      <span class="subtitle-1 font-weight-medium grey--text" color="grey">Find</span>
                      <v-switch v-model="action.actions.find" class="ml-4" inset></v-switch>
                    </template>
                    <v-expansion-panels v-show="action.actions.find">
                      <v-expansion-panel>
                        <v-expansion-panel-header class="px-0">
                          <template v-slot:default="{ open }">
                            <v-row no-gutters>
                              <v-col cols="4">
                                Prompt ID
                              </v-col>
                              <v-col
                                cols="8"
                                class="text--secondary"
                              >
                                <v-fade-transition leave-absolute>
                                  <span
                                    v-if="open"
                                    key="0"
                                  >
                                    Select the elaborator prompt which you want to find the value of
                                  </span>
                                  <span
                                    v-else
                                    key="1"
                                  >
                                    {{ action.properties.findProp && action.properties.findProp.prop }}
                                  </span>
                                </v-fade-transition>
                              </v-col>
                            </v-row>
                          </template>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <v-select
                            :items="elaboratorPromptFind"
                            v-model="action.properties.findProp"
                            item-text="prop"
                            placeholder="Prompt ID"
                            return-object
                          ></v-select>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-banner>
                </v-col>
              </v-row>

              <v-row align="start">
                <v-col cols="12">
                  <v-subheader class="title">Groups</v-subheader>
                  <v-divider></v-divider>
                  <p class="body-2 mt-2">
                    <b>Optionally</b> select a group/s on which this action will trigger. This will also only trigger for members of that group if the action in triggered inside a private chat with the bot.
                  </p>
                </v-col>
                <v-col cols="12" md="6" align-self="start">
                  <v-select
                    v-model="action.trigger_groups"
                    :items="groups"
                    item-text="title"
                    item-value="group_tg_id"
                    attach
                    label="Trigger Group/s"
                    hint="Group/s where the bot will listen for the trigger action."
                    persistent-hint
                    validate-on-blur
                    multiple
                  >
                    <template v-slot:selection="{ item, index }">
                      <v-chip
                        :color="!item.bot_managed ? 'warning':''"
                        v-if="index < 2"
                        :title="!item.bot_managed ? 'This group is not bot managed!':''"
                      >
                        <span>{{ item.title }}</span>
                      </v-chip>
                      <span
                        v-if="index >= 2"
                        class="grey--text caption"
                      >(+{{ action.trigger_groups.length - 2 }} others)</span>
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="12" md="6" align-self="start">
                  <v-alert
                    v-if="action.actions.forward && (!forwardGroups || !forwardGroups.length)"
                    class="body-2"
                    border="left"
                    color="accent"
                    dark
                  >You can only choose the same forward group and trigger group if the action has an elaborator.</v-alert>

                  <v-select
                    v-if="action.actions.forward && forwardGroups && forwardGroups.length"
                    v-model="action.forward_groups"
                    :items="forwardGroups"
                    item-value="group_tg_id"
                    item-text="title"
                    attach
                    :rules="groupRules"
                    label="Forward Group/s*"
                    hint="Group/s where the message (original) or elaborated message (composed from elborator responses) will be forwarded."
                    persistent-hint
                    validate-on-blur
                    multiple
                    @change="forwardGroupsChanged"
                  >
                    <template v-slot:selection="{ item, index }">
                      <v-chip
                        v-if="index < 2"
                        :color="!item.bot_managed ? 'warning':''"
                        :title="!item.bot_managed ? 'This group is not bot managed!':''"
                      >
                        <span>{{ item.title }}</span>
                      </v-chip>
                      <span
                        v-if="index === 2"
                        class="grey--text caption"
                      >(+{{ action.forward_groups.length - 2 }} others)</span>
                    </template>
                  </v-select>

                  <v-row 
                    v-if="action.actions.forward && action.forward_groups && action.forward_groups.length && conditionScenes && conditionScenes.length"
                    class="mt-2" 
                    justify="end"
                  >
                    <forward-condition v-model="action" :groups="groups" :condition-scenes="conditionScenes"></forward-condition>
                  </v-row>

                  <v-col cols="12" v-if="action.forward_group_conditions && Object.keys(action.forward_group_conditions).length">
                    <v-subheader class="body-1">Forward Rules</v-subheader>
                    <v-divider></v-divider>
                    <div
                      v-for="(conditions, id) in forwardGroupConditions"
                      :key="id"
                      class="py-2"
                    >
                      <div
                        v-for="(condition, index) in conditions"
                        :value="condition"
                        :key="index"
                        class="d-flex align-center py-2"
                      >
                        <div class="pr-4">
                          <v-icon color="red" @click="removeCondition(id, index)">mdi-delete</v-icon>
                        </div>
                        <div>
                          <div class="text-subtitle-2">
                            <span v-if="!condition.topic">
                              Forward to <u>{{groupFromId(id)}}</u> when:
                            </span>
                            <span v-if="condition.topic">
                              Forward to <u>{{condition.topic.map(topic => topic.name).join(', ')}}</u> topic/s in <u>{{groupFromId(id)}}</u> when:
                            </span>
                          </div>
                          <div class="text-body-2">
                            <span v-if="condition.prompt_id && condition.value !== undefined && !condition.value.op">
                              <u>{{condition.prompt_id}}</u> is <u>{{Array.isArray(condition.value) ? condition.value.join(' / ') : condition.value}}</u>
                            </span>
                            <span v-if="condition.prompt_id && condition.value !== undefined && condition.value.op">
                              <u>{{condition.prompt_id}}</u> is <u>{{condition.value.op}}</u> to/than <u>{{Array.isArray(condition.value.value) ? condition.value.value.join(' / ') : condition.value.value}}</u>
                            </span>
                            <span v-if="!condition.prompt_id">
                              <u>always</u>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!--<v-list
                      v-for="(conditions, id) in forwardGroupConditions"
                      :key="id"
                      lines="two"
                    >
                      <v-list-item
                        v-for="(condition, index) in conditions"
                        :value="condition"
                        :key="index"
                      >
                        <v-list-item-action>
                          <v-icon color="red" @click="removeCondition(id)"
                            >mdi-delete</v-icon
                          >
                        </v-list-item-action>
                        <v-list-item-title v-if="!condition.topic">
                          Forward to <u>{{groupFromId(id)}}</u> when:
                        </v-list-item-title>
                        <v-list-item-title v-if="condition.topic">
                          Forward to <u>{{condition.topic.map(topic => topic.name).join(', ')}}</u> topic/s in <u>{{groupFromId(id)}}</u> when:
                        </v-list-item-title>
                        <v-list-item-subtitle v-if="!condition.topic && condition.prompt_id && condition.value !== undefined && !condition.value.op">
                          <u>{{condition.prompt_id}}</u> is <u>{{condition.value}}</u>
                        </v-list-item-subtitle>
                        <v-list-item-subtitle v-if="!condition.topic && condition.prompt_id && condition.value !== undefined && condition.value.op">
                          <u>{{condition.prompt_id}}</u> is <u>{{condition.value.op}}</u> to/than <u>{{condition.value.value}}</u>
                        </v-list-item-subtitle>
                        <v-list-item-subtitle v-if="condition.topic && condition.prompt_id && condition.value !== undefined && !condition.value.op">
                          <u>{{condition.prompt_id}}</u> is <u>{{condition.value}}</u>
                        </v-list-item-subtitle>
                        <v-list-item-subtitle v-if="condition.topic && condition.prompt_id && condition.value !== undefined && condition.value.op">
                          <u>{{condition.prompt_id}}</u> is <u>{{condition.value.op}}</u> to/than <u>{{condition.value.value}}</u>
                        </v-list-item-subtitle>
                        <v-list-item-subtitle v-if="condition.topic && !condition.prompt_id">
                          <u>always</u>
                        </v-list-item-subtitle>

                        <v-list-item-action>
                          <v-icon color="red" @click="removeCondition(id)"
                            >mdi-delete</v-icon
                          >
                        </v-list-item-action>
                        <v-list-item-title>
                          {{groupFromId(id)}}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <v-chip v-if="condition.prompt_id" class="subtitle-1 font-weight-bold">{{
                            condition.prompt_id
                          }}</v-chip>
                          <span v-if="condition.topic"><b>Topics:</b> {{Array.isArray(condition.topic)
                            ? condition.topic.join(", ")
                            : condition.topic}}</span>
                        </v-list-item-subtitle>
                        <v-list-item-icon v-if="condition.prompt_id">
                          <v-icon v-if="!condition.value.op">mdi-equal</v-icon>
                          <v-icon v-if="condition.value.op">{{
                            opIcon[condition.value.op]
                          }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-subtitle v-if="condition.prompt_id" class="ml-4 subtitle-1">{{
                          Array.isArray(condition.value)
                            ? condition.value.join(" / ")
                            : condition.value.op
                            ? condition.value.value
                            : condition.value
                            ? "Yes"
                            : "No"
                        }}</v-list-item-subtitle>
                      </v-list-item>
                    </v-list>-->
                  </v-col>

                  <!--<pre>conditionScenes: {{conditionScenes}}</pre>-->
                </v-col>
              </v-row>
            </v-container>
            <small>*indicates required field</small>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" class="ma-2" outlined type="submit" :loading="saving" v-if="!action.id">Create Action</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ForwardCondition from "./ForwardCondition";
import Emoji from "../widget/Emoji";
import PickDate from "../widget/PickDate";
import PickTime from "../widget/PickTime";
import TimeoutMessage from "../widget/TimeoutMessage";

export default {
  data: () => ({
    keywords: [],
    saving: false,
    showSaving: false,
    needsApply: false,
    useConditionalForwarding: false,
    conditionalForwardingScene: {},
    conditionalForwarding: {
      group_tg_id: null,
      value: null
    },
    valid: false,
    error: {},
    errors: {
      email: "",
      server: "",
    },
    setIcon: false,
    media: [{ text: "Photo", value: "photo" }, { text: "Voice Note", value: "voice" }],
    scheduleIntervals: [
      { text: "Daily", value: "daily" },
      { text: "Weekly", value: "weekly" },
      { text: "Monthly", value: "monthly" },
    ],
    special: [
      { text: "License Plate", value: "license_plate" },
      { text: "Profane Language", value: "profanity" },
    ],
    commandRules: [
      (v) => !!v || "Command is required",
      (v) =>
        (v && v.split(" ").length === 1) ||
        "A command can only consist of a single word",
      (v) =>
        (v && v.indexOf("/") === -1) ||
        "Please enter the command without the '/'",
    ],
    referenceRules: [
      (v) => !!v || "Reference is required",
      (v) => (v && v.length <= 3) || "Reference must be 1-3 letters long",
    ],
    keywordsRules: [
      (v) => !!v.length || "Keyword/s are required",
      (v) => (v && v.length <= 5) || "You cannot use more than 5 keywords",
    ],
    groupRules: [
      (v) => (v && !!v.length) || "Please select at least one group",
    ],
  }),
  created() {},
  mounted() {
    if (!this.action.properties) this.action.properties = {};
    if (!this.action.properties.icon) this.action.properties.icon = "";

    this.debouncedSave = this.$debounce(this.saveAction, 3000);
  },
  computed: {
    ...mapGetters("apiAuth", ["user"]),
    ...mapGetters("emoji", ["emojiRegex"]),
    iconRules() {
      return [(v) => !v || (v && this.emojiRegex.test(v)) || "Icon only"];
    },
    isIconOnly() {
      return this.action && this.action.properties
        ? this.emojiRegex.test(this.action.properties.icon)
        : false;
    },
    isWebhooksEnabled() {
      return (
        this.features && this.features.config && this.features.config.webhooks
      );
    },
    isProcessFilesEnabled() {
      return (
        this.features && this.features.config && this.features.config.process_files
      );
    },
    isActionsMapEnabled() {
      return (
        this.features &&
        this.features.config &&
        this.features.config.actions_map
      );
    },
    elboratorRequired() {
      return ["command", "schedule"].includes(this.action.trigger.type);
    },
    elaboratorRules() {
      return [
        (v) =>
          (v && !!v.length) ||
          !this.elboratorRequired ||
          "Setting an elaborator is required for the chosen trigger type",
      ];
    },
    elaboratorPromptFind() {
      if (this.selectedElaborator) {
        console.log('this.selectedElaborator', this.selectedElaborator)
        return this.selectedElaborator.scenes
          .filter(scene => scene.type === 'license_plate' || scene.type === 'reference')
          .map(scene => { return { prop: scene.id, type: scene.type} })
      }

      return []
    },
    isScheduledActionsEnabled() {
      return (
        this.features &&
        this.features.config &&
        this.features.config.scheduled_actions
      );
    },
    selectedElaborator() {
      let elaborator = null;
      if (
        this.action.elaborator_id === undefined ||
        this.action.elaborator_id === null
      )
        return elaborator;

      this.elaborators.forEach((elab) => {
        if (this.action.elaborator_id === elab.id) {
          elaborator = elab;
        }
      });

      return elaborator;
    },
    conditionScenes() {
      const conditionScenes = [];

      if (!this.selectedElaborator) return conditionScenes;

      this.selectedElaborator.scenes.forEach((scene) => {
        if (["text", "select", "binary"].includes(scene.type)) {
          if (
            scene.type !== "text" ||
            (scene.type === "text" &&
              scene.validation &&
              scene.validation.expect === "number")
          ) {
            conditionScenes.push(scene);
          }
        }
      });

      return conditionScenes;
    },
    forwardGroups() {
      if (this.action.elaborator_id) return this.groups;

      let intersected = this.groups.filter((group) =>
        this.action.trigger_groups.includes(group.id)
      );
      let intersectedIds = intersected.map((group) => group.id);
      return this.groups.filter((group) => !intersectedIds.includes(group.id));
    },
    bot() {
      return this.user &&
        this.user.account &&
        this.user.account.bots &&
        this.user.account.bots.length
        ? this.user.account.bots[0]
        : null;
    },
    forwardGroupConditions() {
      const conditions = {}

      Object.keys(this.action.forward_group_conditions).forEach(id => {
        conditions[id] = Array.isArray(this.action.forward_group_conditions[id]) ? this.action.forward_group_conditions[id] : [this.action.forward_group_conditions[id]]
      })

      return conditions
    }
  },
  methods: {
    ...mapActions("app", ["showMessage"]),
    ...mapActions("heroku", { reboot: "remove" }),
    async addCondition() {
      const condition = {
        prompt_id: this.conditionalForwardingScene.id,
        ...this.conditionalForwarding,
      };

      this.action.forward_group_conditions[this.conditionalForwarding.group_tg_id] = condition;

      await this.saveAction();

      this.conditionalForwardingScene = {};
      this.conditionalForwarding = {
        group_tg_id: null,
        value: null
      };
    },
    async removeCondition(id, index) {
      if (this.action.forward_group_conditions[id]) {
        if (Array.isArray(this.action.forward_group_conditions[id])) {
          this.action.forward_group_conditions[id].splice(index, 1)
        } else {
          delete this.action.forward_group_conditions[id];
        }

        await this.saveAction();
      }
    },
    forwardGroupsChanged(items) {
      if (this.action.forward_group_conditions) {
        for (const groupId in this.action.forward_group_conditions) {
          if (Object.hasOwnProperty.call(this.action.forward_group_conditions, groupId)) {
            if (!items.includes(groupId)) {
              delete this.action.forward_group_conditions[groupId];
            }            
          }
        }
      }
    },
    forwardingGroups(groups) {
      if (!groups || !groups.length) return [];

      return this.groups.filter((group) => groups.includes(group.group_tg_id));
    },
    groupFromId(id) {
      const group = this.groups.find((group) => group.group_tg_id.toString() === id.toString());
      return group && group.title || 'None';
    },
    validate() {
      return this.$refs.form.validate();
    },
    resetValidation() {
      this.$refs.form.resetValidation();
      this.errors = {};
    },
    titleCase(field) {
      this.action[field] = this.$options.filters.title_case(this.action[field]);
    },
    async saveAction(auto, action) {
      if (!this.validate()) {
        if (!auto)
          this.showMessage({ text: "You missed something!", color: "error" });
        return;
      }

      this.saving = true;
      this.showSaving = true;
      try {
        if (action) {
          await action.save();
          action.__hasChanges = false;
        }
        else await this.action.save();
        //this.showMessage({ text: "Saved!", color: "success" });
      } catch (error) {
        this.error = error;
        this.showMessage({
          text: error.message || "Error occured!",
          color: "error",
        });
      }
      this.saving = false;
      this.needsApply = true;
    },
    async applyChanges() {
      if (this.bot) {
        this.showMessage({
          text: "Changes are being applied...",
          timeout: 12000,
          position: "top",
        });
        await this.reboot(this.bot.app_id);
        this.needsApply = false;
      }
    },
  },
  watch: {
    "conditionalForwardingScene.type": {
      handler(type) {
        if (type === "text") this.conditionalForwarding.value = {};
        if (type !== "text") this.conditionalForwarding.value = null;
      },
    },
    action: {
      handler(now, before) {
        if (now && now.properties.hidden === undefined) {
          now.properties.hidden = false;
          this.debouncedSave(true, now);
        }

        if (now.id && now.id !== before.id) {
          if (before.id && before.__hasChanges) {
            this.debouncedSave(true, before);
          }
        } else if (now.id) {
          now.__hasChanges = true;
          this.debouncedSave(true, now);
        }

        if (!this.valid) {
          this.resetValidation();
        }
      },
      deep: true,
    },
    forwardGroups(groups) {
      if (!groups || (!groups.length && this.action.forward_groups.length))
        this.action.forward_groups = [];
    },
    "action.actions.forward": {
      handler(forward) {
        if (forward === false) this.action.forward_groups = [];
      },
    },
    "action.elaborator_id": {
      handler(hasElaborator) {
        if (hasElaborator) {
          this.action.actions.remove = false;
        }
      },
    },
  },
  beforeDestroy() {
    if (this.needsApply) this.applyChanges();
  },
  components: {
    ForwardCondition,
    Emoji,
    PickDate,
    PickTime,
    TimeoutMessage
  },
  props: ["features", "action", "elaborators", "groups", "actionElaborators"],
};
</script>